import { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEstablishSession, useLogout } from '../features/session.js';
import { useSession } from '../stores/session.js';
import { useUi } from '../stores/ui.js';
import { Spinner } from '../components/ui/Spinner.jsx';
import { Toaster } from '../components/ui/Toast.jsx';
import { ScrollToTop } from './ScrollToTop.jsx';
import { ConfirmDialog } from '../components/ui/Modal.jsx';
import { Badge } from '../components/ui/Badge.jsx';
import { cn } from '../lib/format.js';
import { storefrontUrl } from '../lib/storefront.js';

/**
 * The admin shell.
 *
 * Navigation is grouped by what an operator is doing rather than by which
 * backend module owns the data, and every entry is filtered by permission — a
 * role that cannot read coupons never sees the link, and the server refuses the
 * route regardless.
 */

const NAV = [
  {
    group: 'Overview',
    items: [{ to: '/', label: 'Dashboard', end: true, permission: ['dashboard', 'read'] }],
  },
  {
    group: 'Selling',
    items: [
      /* `end` so the section link does not stay lit while Receivables is open. */
      { to: '/orders', label: 'Orders', end: true, permission: ['orders', 'read'] },
      { to: '/orders/receivables', label: 'Receivables', permission: ['orders', 'read'] },
      { to: '/customers', label: 'Customers', permission: ['customers', 'read'] },
      { to: '/newsletter', label: 'Newsletter', permission: ['customers', 'read'] },
      { to: '/coupons', label: 'Coupons', permission: ['coupons', 'read'] },
      { to: '/reviews', label: 'Reviews', permission: ['reviews', 'read'] },
    ],
  },
  {
    group: 'Catalogue',
    items: [
      { to: '/products', label: 'Products', permission: ['products', 'read'] },
      { to: '/collections', label: 'Collections', permission: ['categories', 'read'] },
      { to: '/categories', label: 'Categories', permission: ['categories', 'read'] },
      { to: '/brands', label: 'Brands', permission: ['brands', 'read'] },
      { to: '/attributes', label: 'Attributes', permission: ['attributes', 'read'] },
      { to: '/media', label: 'Media', permission: ['media', 'read'] },
    ],
  },
  {
    group: 'Content',
    items: [
      { to: '/blog', label: 'Blogs', permission: ['blog', 'read'] },
      { to: '/pages', label: 'Pages', permission: ['pages', 'read'] },
      { to: '/enquiries', label: 'Enquiries', permission: ['pages', 'read'] },
    ],
  },
  {
    group: 'Store',
    items: [
      { to: '/settings', label: 'Settings', permission: ['settings', 'read'] },
      { to: '/staff', label: 'Staff', permission: ['staff', 'read'] },
      { to: '/activity', label: 'Activity log', permission: ['activity', 'read'] },
    ],
  },
];

export const Shell = () => {
  const location = useLocation();
  const { isPending } = useEstablishSession();

  const status = useSession((state) => state.status);
  const admin = useSession((state) => state.admin);
  const can = useSession((state) => state.can);

  const sidebarOpen = useUi((state) => state.sidebarOpen);
  const closeSidebar = useUi((state) => state.closeSidebar);
  const toggleSidebar = useUi((state) => state.toggleSidebar);
  const confirmation = useUi((state) => state.confirmation);

  const logout = useLogout();

  useEffect(() => closeSidebar(), [location.pathname, closeSidebar]);

  if (status === 'unknown' || isPending) {
    return (
      <div className="grid min-h-screen place-items-center bg-paper">
        <Spinner className="size-6 text-muted" label="Checking your session" />
      </div>
    );
  }

  if (status !== 'authenticated') {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item.permission[0], item.permission[1])),
  })).filter((group) => group.items.length > 0);

  return (
    <>
      <ScrollToTop />

      <Helmet>
        <title>Golden Era — Operations</title>
      </Helmet>

      <div className="flex min-h-screen bg-paper">
        {/*
          `lg:sticky` with an explicit height, not `lg:static`.

          As a static element the sidebar was a normal block in a `min-h-screen`
          flex row, so it scrolled away with the page — on a long orders table the
          navigation was simply gone. Sticky pins it to the top of the viewport and
          `h-screen` gives it a height to work against, which is what lets the
          `overflow-y-auto` on the nav below scroll the links independently when
          there are more than fit.
        */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-mist bg-surface transition-transform',
            'lg:sticky lg:top-0 lg:h-screen lg:translate-x-0',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-12 items-center gap-2.5 border-b border-mist px-4">
            <img
              src="/brand/golden-era-mark.png"
              alt=""
              width="80"
              height="96"
              className="h-7 w-auto select-none"
            />
            <span className="font-display text-2xs font-semibold tracking-[0.14em] text-ink uppercase">
              Golden Era
            </span>
          </div>

          <nav aria-label="Sections" className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
            {groups.map((group) => (
              <div key={group.group} className="mb-4 last:mb-0">
                <p className="px-2 pb-1.5 font-mono text-[11px] tracking-[0.14em] text-muted uppercase">
                  {group.group}
                </p>
                <ul className="space-y-px">
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            'block rounded px-2 py-1.5 text-xs transition-colors',
                            'focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-signal',
                            isActive ? 'bg-ink text-white' : 'text-muted hover:bg-mist/50 hover:text-ink',
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-mist p-3">
            <div className="flex items-center gap-2">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-mist font-mono text-2xs text-ink">
                {(admin?.name ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-2xs text-ink">{admin?.name}</p>
                <Badge tone="muted" className="mt-0.5">
                  {admin?.role}
                </Badge>
              </div>
            </div>

            <div className="mt-2.5 space-y-px">
              {/* Beside Sign out, because that is where somebody looks for
                  anything to do with their own account. */}
              <NavLink
                to="/account"
                className={({ isActive }) =>
                  cn(
                    'block rounded px-2 py-1.5 text-2xs transition-colors',
                    isActive ? 'bg-ink text-white' : 'text-muted hover:bg-mist/50 hover:text-ink',
                  )
                }
              >
                Your account
              </NavLink>

              <button
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                className="w-full rounded px-2 py-1.5 text-left text-2xs text-muted transition-colors hover:bg-mist/50 hover:text-ink"
              >
                Sign out
              </button>
            </div>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="fixed inset-0 z-30 bg-ink/30 lg:hidden" onClick={closeSidebar} aria-hidden="true" />
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-12 items-center gap-3 border-b border-mist bg-surface px-3">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Toggle navigation"
              className="grid size-8 place-items-center rounded text-muted hover:bg-mist/50 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal lg:hidden"
            >
              <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden="true">
                <path d="M2.5 5h15v1.5h-15zm0 4.25h15v1.5h-15zm0 4.25h15V15h-15z" />
              </svg>
            </button>

            <a
              href={storefrontUrl('') ?? '/'}
              target="_blank"
              rel="noreferrer noopener"
              className="ml-auto text-2xs text-muted transition-colors hover:text-ink"
            >
              View the store ↗
            </a>
          </header>

          <main className="min-h-0 flex-1">
            <Suspense
              fallback={
                <div className="grid min-h-[60vh] place-items-center">
                  <Spinner className="size-5 text-muted" label="Loading" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <Toaster />
      <ConfirmDialog confirmation={confirmation} />
    </>
  );
};

/**
 * A route guard. The server checks the same permission on every request; this
 * exists so an operator sees an explanation instead of a wall of 403s.
 */
export const RequirePermission = ({ module, action = 'read', children }) => {
  const can = useSession((state) => state.can);

  if (!can(module, action)) {
    return (
      <div className="grid min-h-[60vh] place-items-center px-6">
        <div className="max-w-sm text-center">
          <h1 className="text-sm font-medium text-ink">You do not have access to this</h1>
          <p className="mt-2 text-xs leading-relaxed text-muted">
            Your role cannot {action} {module}. An administrator can grant it from Staff.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default Shell;
